import { IconRatingThumb } from 'assets/img';
import { ContextMenuButton, LwIconButton, TableHeader } from 'redesign';
import { formatDuration, formatTime } from 'shared/utils/formatting';
import type { Column } from 'react-table';
import { styled, Typography } from '@mui/material';
import { TFunctionNonStrict } from 'i18next';
import { InboxCellProps, InboxTableColumnDefinition } from './inbox-table.types';

import {
  getCheckoutsColumnDefinitionBase,
  ScheduledTimesCell,
} from '../../../../shared/columns/get-checkouts-column-definition-base';
import {
  CheckoutsCellProp,
  CheckoutsColumnBase,
} from '../../../../shared/columns/get-checkouts-column-definition-base.types';
import { HighlightBox } from '../../../../shared/components/highlight-box';
import { getShiftDurationInMs } from '../../../../shared/utils/get-shift-duration-in-ms';

export const getInboxColumnDefinition: ({
  allowExpenses,
  hideLimitedCompanyShiftFields,
  t,
}: {
  allowExpenses: boolean;
  hideLimitedCompanyShiftFields?: boolean;
  t: TFunctionNonStrict<'ogp-screen-specific', undefined>;
}) => Column<InboxTableColumnDefinition>[] = ({
  allowExpenses,
  hideLimitedCompanyShiftFields = false,
  t,
}) => {
  const worked: Column<CheckoutsColumnBase> = {
    Header: () => (
      <>
        <TableHeader>
          {t('checkouts.shifts.table_headers.working_hours', { ns: 'ogp-screen-specific' })}
        </TableHeader>
        <TableHeader>
          {t('checkouts.shifts.table_headers.time_worked', { ns: 'ogp-screen-specific' })}
        </TableHeader>
      </>
    ),
    accessor: 'workedTime',
    Cell: WorkedTimeCell,
  };

  const rating: Column<InboxTableColumnDefinition> = {
    Header: () => (
      <TableHeader>
        {t('checkouts.shifts.table_headers.rating', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'rating',
    Cell: RatingCell,
  };

  const actions: Column<InboxTableColumnDefinition> = {
    accessor: 'actions',
    Header: '',
    Cell: ActionCell,
  };

  const employmentType: Column<InboxTableColumnDefinition> = {
    Header: () => (
      <TableHeader>
        {t('checkouts.shifts.table_headers.employment_type', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'placementEmploymentType',
    Cell: EmploymentTypeCell,
  };

  const { fw, expense, job, date, scheduled, timeDeviationAndBreak, branchOffice, department } =
    getCheckoutsColumnDefinitionBase(allowExpenses, t);

  return [
    fw,
    rating,
    ...(hideLimitedCompanyShiftFields ? [] : [job, branchOffice, department]),
    employmentType,
    date,
    scheduled,
    worked,
    expense,
    timeDeviationAndBreak,
    actions,
  ];
};

const RatingCell = (cell: InboxCellProps<'rating'>) => {
  const isDisabled = cell.row.original.actions.isBorrowedWorkerShift;
  return (
    <StyledIconButton
      aria-label="rating"
      iconColorMode="stroke"
      onClick={() => cell.onChangeRating(cell.value.id)}
      isThumbsUp={cell.value.value !== undefined && cell.value.value}
      disabled={isDisabled}
    >
      <IconRatingThumb title="icon-rating" />
    </StyledIconButton>
  );
};

const ActionCell = (cell: InboxCellProps<'actions'>) => {
  const actions = cell.getRowActions(cell.value);

  if (actions.length === 0) {
    return null;
  }

  return (
    <ContextMenuButton menuId={`claimant-actions-menu-${cell.value.worker.workerId}`}>
      {actions}
    </ContextMenuButton>
  );
};

const EmploymentTypeCell = (cell: InboxCellProps<'placementEmploymentType'>) => {
  return <Typography>{cell.value}</Typography>;
};
const WorkedTimeCell = (cell: CheckoutsCellProp<'workedTime'>) => {
  const { timeZone, startDate, endDate, breakInMinutes, timeDeviationInMs } = cell.value;
  const workedTime = getShiftDurationInMs(startDate, endDate, breakInMinutes);
  const timeRange = `${formatTime(startDate, timeZone)} - ${formatTime(endDate, timeZone)}`;

  return timeDeviationInMs ? (
    <HighlightBox>
      {timeRange}
      {formatDuration({ milliseconds: workedTime })}
    </HighlightBox>
  ) : (
    <ScheduledTimesCell {...cell} />
  );
};

const StyledIconButton = styled(LwIconButton, {
  shouldForwardProp: (prop) => prop !== 'isThumbsUp',
})<{ isThumbsUp: boolean }>(({ isThumbsUp }) => ({
  padding: 0,
  animation: `${isThumbsUp ? 'spinUp' : 'spinDown'} .2s linear`,
  transform: isThumbsUp ? 'rotate(0deg)' : 'rotate(180deg)',

  '@keyframes spinUp': {
    '0%': {
      transform: 'rotate(-180deg)',
    },
    '100%': {
      transform: 'rotate(0deg)',
    },
  },

  '@keyframes spinDown': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(-180deg)',
    },
  },
}));
